interface AutocompleteConcept {
  title: string;
  slug: string;
  aliases?: string[];
}

export function attachConceptAutocomplete(input: HTMLInputElement, concepts: AutocompleteConcept[]): { destroy: () => void; updateConcepts: (newConcepts: AutocompleteConcept[]) => void } {
  let _concepts = concepts;
  const wrapper = input.parentElement!;
  wrapper.style.position = 'relative';

  const dropdown = document.createElement('div');
  dropdown.className = 'concept-ac';
  dropdown.style.display = 'none';
  wrapper.appendChild(dropdown);

  let isOpen = false;
  let focusedIndex = -1;
  let bracketStart = -1;

  function getQuery(): string | null {
    const val = input.value;
    const cursor = input.selectionStart ?? 0;
    const before = val.slice(0, cursor);
    const idx = before.lastIndexOf('[[');
    if (idx === -1) return null;
    const between = before.slice(idx + 2);
    if (between.includes(']]')) return null;
    bracketStart = idx;
    return between;
  }

  function findMatchedAlias(c: AutocompleteConcept, q: string): string | null {
    if (!q || !c.aliases) return null;
    for (const a of c.aliases) {
      if (a.toLowerCase().includes(q)) return a;
    }
    return null;
  }

  function getFilteredConcepts(query: string | null): { concept: AutocompleteConcept; matchedAlias: string | null }[] {
    const q = (query || '').toLowerCase();
    if (!q) return _concepts.slice(0, 20).map(c => ({ concept: c, matchedAlias: null }));
    return _concepts.filter(c =>
      c.title.toLowerCase().includes(q) ||
      c.slug.toLowerCase().includes(q) ||
      (c.aliases || []).some(a => a.toLowerCase().includes(q))
    ).sort((a, b) => {
      const aTitle = a.title.toLowerCase(), bTitle = b.title.toLowerCase();
      const aSlug = a.slug.toLowerCase(), bSlug = b.slug.toLowerCase();
      const aExact = aTitle === q || aSlug === q;
      const bExact = bTitle === q || bSlug === q;
      if (aExact !== bExact) return aExact ? -1 : 1;
      const aStarts = aTitle.startsWith(q) || aSlug.startsWith(q);
      const bStarts = bTitle.startsWith(q) || bSlug.startsWith(q);
      if (aStarts !== bStarts) return aStarts ? -1 : 1;
      return 0;
    }).slice(0, 20).map(c => {
      const nameMatches = c.title.toLowerCase().includes(q) || c.slug.toLowerCase().includes(q);
      return { concept: c, matchedAlias: nameMatches ? null : findMatchedAlias(c, q) };
    });
  }

  function render(query: string | null) {
    const filtered = getFilteredConcepts(query);
    focusedIndex = -1;

    if (filtered.length === 0) {
      dropdown.innerHTML = '<div class="concept-ac__empty">no matches</div>';
    } else {
      dropdown.innerHTML = filtered.map(({ concept: c, matchedAlias }, i) =>
        `<div class="concept-ac__option" data-slug="${c.slug}" data-index="${i}">`
        + `<span class="concept-ac__title">${c.title}</span>`
        + (matchedAlias ? `<span class="concept-ac__alias">alias: ${matchedAlias}</span>` : '')
        + `<span class="concept-ac__slug">${c.slug}</span>`
        + `</div>`
      ).join('');
    }
  }

  function open(query: string | null) {
    render(query);
    dropdown.style.display = 'block';
    isOpen = true;
  }

  function close() {
    dropdown.style.display = 'none';
    isOpen = false;
    focusedIndex = -1;
    bracketStart = -1;
  }

  function insertSlug(slug: string) {
    const val = input.value;
    const cursor = input.selectionStart ?? 0;
    const before = val.slice(0, bracketStart);
    const after = val.slice(cursor);
    const inserted = `[[${slug}]]`;
    input.value = before + inserted + after;
    const newPos = bracketStart + inserted.length;
    input.setSelectionRange(newPos, newPos);
    input.focus();
    close();
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  function setFocused(idx: number) {
    const items = dropdown.querySelectorAll('.concept-ac__option');
    for (const item of items) item.classList.remove('concept-ac__option--focused');
    focusedIndex = idx;
    if (idx >= 0 && idx < items.length) {
      items[idx].classList.add('concept-ac__option--focused');
      items[idx].scrollIntoView({ block: 'nearest' });
    }
  }

  input.addEventListener('input', () => {
    const query = getQuery();
    if (query !== null) {
      open(query);
    } else if (isOpen) {
      close();
    }
  });

  input.addEventListener('keydown', (e) => {
    if (!isOpen) return;

    const items = dropdown.querySelectorAll('.concept-ac__option') as NodeListOf<HTMLElement>;
    const count = items.length;

    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (count > 0) setFocused(focusedIndex < count - 1 ? focusedIndex + 1 : 0);
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (count > 0) setFocused(focusedIndex > 0 ? focusedIndex - 1 : count - 1);
      return;
    }

    if (e.key === 'Enter' || e.key === 'Tab') {
      if (focusedIndex >= 0 && focusedIndex < count) {
        e.preventDefault();
        insertSlug(items[focusedIndex].dataset.slug!);
        return;
      }
      if (e.key === 'Enter') {
        close();
      }
    }
  });

  dropdown.addEventListener('mousedown', (e) => {
    const opt = (e.target as HTMLElement).closest('.concept-ac__option') as HTMLElement | null;
    if (opt) {
      e.preventDefault();
      insertSlug(opt.dataset.slug!);
    }
  });

  input.addEventListener('blur', () => {
    setTimeout(close, 200);
  });

  input.addEventListener('focus', () => {
    const query = getQuery();
    if (query !== null) open(query);
  });

  return {
    destroy() {
      dropdown.remove();
    },
    updateConcepts(newConcepts: AutocompleteConcept[]) {
      _concepts = newConcepts;
      if (isOpen) {
        const query = getQuery();
        if (query !== null) render(query);
      }
    },
  };
}
